// Reactivate my code (FR-31): a suspended individual can sign in and lift the
// suspension after step-up "suspend". When the platform put an admin hold on
// the account the server refuses (403) and only support can help.
import { Alert, Button, Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { useBrand } from "../../shared/brand/brand";
import { errorMessage } from "../../shared/session/errors";
import { ME_KEY } from "../../shared/session/me";
import { useStepUp } from "../../shared/session/stepup";
import { FormError } from "../../shared/ui/Page";

export function ReactivateBanner({ onReactivated }: { onReactivated: () => void }) {
  const { t } = useTranslation();
  const brand = useBrand();
  const stepUp = useStepUp();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reactivate = async () => {
    setError(null);
    const token = await stepUp("suspend");
    if (!token) return;
    setBusy(true);
    const { response, error } = await api.POST("/me/reactivate", {
      params: { header: { "X-Step-Up-Token": token } },
    });
    setBusy(false);
    if (response.status === 403) {
      return setError(t("reactivate.admin_hold", { email: brand.support.email, phone: brand.support.phone }));
    }
    if (response.status === 409) {
      // Not suspended any more (another tab, or support): show the account as it is.
      setError(t("reactivate.not_suspended"));
      return void queryClient.invalidateQueries({ queryKey: ME_KEY });
    }
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    onReactivated();
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
  };

  return (
    <Alert color="orange" title={t("reactivate.title")} data-testid="reactivate-banner">
      <Stack gap="sm">
        <Text size="sm">{t("reactivate.body")}</Text>
        <FormError message={error} />
        <Button w="fit-content" loading={busy} onClick={() => void reactivate()}>
          {t("reactivate.button")}
        </Button>
      </Stack>
    </Alert>
  );
}
